import { Link } from "react-router-dom";
import { Brain, GitBranch, Hourglass, Users } from "lucide-react";
import { useWorld } from "@/context/WorldContext";
import { SPECIES_STATUSES } from "@/lib/constants";
import { isSapient, lifespanOf, relatedSpecies, speciesMembers, speciesStatus } from "@/lib/infobox";
import { Portrait } from "@/components/ShipBits";

const chip = "inline-flex items-center gap-1 text-[10px] font-code uppercase tracking-wider rounded-full border px-2 py-0.5";

export const SpeciesStatusBadge = ({ status }) => (
  <span className={`${chip} ${SPECIES_STATUSES[status].cls}`} data-testid="species-status-badge">
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: SPECIES_STATUSES[status].hex }} />{status}
  </span>
);

export const SapientBadge = ({ on }) => (
  <span className={`${chip} ${on ? "text-sky-200 border-sky-500/40 bg-sky-500/10" : "text-slate-400 border-line"}`} data-testid="species-sapient-badge">
    <Brain className="w-3 h-3" />{on ? "Sapient" : "Non-sapient"}
  </span>
);

export const SpeciesMeta = ({ page, className = "" }) => {
  const { pages } = useWorld();
  const life = lifespanOf(page);
  const n = speciesMembers(pages, page.id).length;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="species-meta">
      <SpeciesStatusBadge status={speciesStatus(page)} />
      <SapientBadge on={isSapient(page)} />
      {life && <span className="inline-flex items-center gap-1 text-[11px] font-code text-slate-300" data-testid="species-lifespan"><Hourglass className="w-3 h-3 text-sky-400" />{life}</span>}
      <span className="inline-flex items-center gap-1 text-[11px] font-code text-slate-400" data-testid="species-member-count"><Users className="w-3 h-3" />{n} {n === 1 ? "member" : "members"}</span>
    </div>
  );
};

const Panel = ({ icon: Icon, title, testId, children }) => (
  <div className="rounded-xl border border-sky-500/30 bg-panel p-4" data-testid={testId}>
    <h3 className="font-display text-lg text-sky-100 mb-3 flex items-center gap-2"><Icon className="w-4 h-4 text-sky-400" /> {title}</h3>
    {children}
  </div>
);

export const MembersPanel = ({ page }) => {
  const { pages, basePath } = useWorld();
  const members = speciesMembers(pages, page.id).sort((a, b) => a.title.localeCompare(b.title));
  return (
    <Panel icon={Users} title={`Members · ${members.length}`} testId="species-members-panel">
      {members.length === 0 ? <p className="text-xs text-slate-500 italic">Link a character's Species fact to this page to list them here.</p> : (
        <div className="space-y-1.5">
          {members.map((m) => (
            <Link key={m.id} to={`${basePath}/p/${m.id}`} className="flex items-center gap-2.5 group" data-testid={`species-member-${m.id}`}>
              <Portrait page={m} size="w-8 h-8 text-[11px]" className="ring-1 ring-sky-500/30 group-hover:ring-sky-400 transition-shadow" />
              <span className="text-sm text-slate-200 group-hover:text-sky-300 transition-colors truncate">{m.title}</span>
            </Link>
          ))}
        </div>
      )}
    </Panel>
  );
};

export const RelatedSpeciesPanel = ({ page }) => {
  const { pages, basePath } = useWorld();
  const related = relatedSpecies(pages, page);
  if (!related.length) return null;
  return (
    <Panel icon={GitBranch} title="Related species" testId="species-related-panel">
      <div className="space-y-2">
        {related.map((r) => (
          <Link key={r.page.id} to={`${basePath}/p/${r.page.id}`} className="flex items-center gap-2 group" data-testid={`species-related-${r.page.id}`}>
            <span className="font-code text-[10px] uppercase tracking-wider text-slate-500 w-24 shrink-0" data-testid={`species-related-label-${r.page.id}`}>{r.label}</span>
            <span className="text-sm text-slate-200 group-hover:text-sky-300 transition-colors truncate">{r.page.title}</span>
          </Link>
        ))}
      </div>
    </Panel>
  );
};
